// Оркестрація офлайну: скачати/оновити пакет питань (+ закешувати картинки),
// і досилати на сервер проходження, зроблені офлайн (outbox).
// Викликається з бут-коду / UI — НЕ з api.ts (щоб не було циклічного імпорту).
import { api } from './api';
import {
  getPackVersion,
  savePack,
  getOutbox,
  removeFromOutbox,
  getMeta,
  setMeta,
  allQuestions,
  isPackReady,
} from './offlineDb';

// Повністю готово до офлайну = пакет збережено І всі картинки закешовані для цієї версії
export async function isOfflineReady(): Promise<boolean> {
  if (!(await isPackReady())) return false;
  const pv = await getPackVersion();
  const iv = await getMeta<string>('imagesVersion');
  return !!pv && pv === iv;
}

// Той самий кеш, з якого service worker віддає картинки офлайн (/uploads/*).
const IMAGE_CACHE = 'pdr-images-v1';

export type DownloadProgress = { phase: 'pack' | 'images' | 'done'; done: number; total: number };

// Скачати/оновити пакет + прогріти кеш картинок. onProgress — для екрана «Завантаження».
export async function downloadPack(
  onProgress?: (p: DownloadProgress) => void,
): Promise<'updated' | 'fresh' | 'skipped'> {
  if (!navigator.onLine) return 'skipped';
  const current = await getPackVersion();
  onProgress?.({ phase: 'pack', done: 0, total: 1 });

  let pack;
  try {
    pack = await api.offline.pack(current);
  } catch {
    return 'skipped';
  }

  // Питання, чиї картинки треба закешувати, + версія
  let questions;
  let version: string;
  const isFresh = pack === null;
  if (pack === null) {
    // 304 — база актуальна. Якщо картинки для цієї версії вже докачані — виходимо.
    const imagesVer = await getMeta<string>('imagesVersion');
    if (imagesVer === current) {
      onProgress?.({ phase: 'done', done: 1, total: 1 });
      return 'fresh';
    }
    // інакше — докачуємо картинки (перше завантаження перервалось)
    questions = await allQuestions();
    version = current ?? '';
  } else {
    await savePack(pack);
    onProgress?.({ phase: 'pack', done: 1, total: 1 });
    questions = pack.questions;
    version = pack.version;
  }

  // Кешуємо ВСІ картинки з бази (щоб знаки/малюнки були офлайн)
  const urls = Array.from(
    new Set(questions.map((q) => q.imageUrl).filter((u): u is string => !!u)),
  );
  if (typeof caches !== 'undefined' && urls.length) {
    try {
      // Пауза перед картинками: даємо власним запитам апки (профіль, категорії)
      // пройти першими — на старих/повільних пристроях масове скачування
      // картинок інакше блокує весь інтерфейс на спінері.
      await new Promise((r) => setTimeout(r, 2500));
      const cache = await caches.open(IMAGE_CACHE);
      let done = 0;
      let idx = 0;
      const total = urls.length;
      onProgress?.({ phase: 'images', done: 0, total });
      // Паралельно, але помірно (3 потоки) — швидше, ніж по одній, і не душить старі пристрої.
      const worker = async () => {
        while (idx < total) {
          const u = urls[idx++]!;
          try {
            if (!(await cache.match(u))) {
              const res = await fetch(u);
              if (res.ok) await cache.put(u, res.clone());
            }
          } catch {
            /* бита картинка — пропускаємо */
          }
          done++;
          if (done % 15 === 0 || done === total) {
            onProgress?.({ phase: 'images', done, total });
          }
        }
      };
      await Promise.all(Array.from({ length: 3 }, () => worker()));
    } catch {
      /* Cache API недоступний — картинки просто не закешуються */
    }
  }
  // Позначаємо, що картинки цієї версії докачані повністю
  await setMeta('imagesVersion', version);
  onProgress?.({ phase: 'done', done: 1, total: 1 });
  return isFresh ? 'fresh' : 'updated';
}

// Досилаємо офлайн-проходження на сервер (ідемпотентно за Attempt.id)
export async function syncOutbox(): Promise<number> {
  if (!navigator.onLine) return 0;
  const items = await getOutbox();
  if (!items.length) return 0;
  try {
    const res = await api.offline.sync(items);
    // Прибираємо все відправлене (synced/duplicate/invalid) — щоб не застрягало в черзі
    for (const it of items) await removeFromOutbox(it.id);
    return res.synced;
  } catch {
    return 0; // немає зв'язку/помилка — спробуємо наступного разу
  }
}

let bootstrapped = false;
// Виклик на старті (онлайн) і по події `online`: спершу зливаємо чергу, потім оновлюємо базу.
export async function offlineBootstrap(onProgress?: (p: DownloadProgress) => void): Promise<void> {
  await syncOutbox();
  await downloadPack(onProgress);
}

// Один раз на завантаження сторінки навісити слухач повернення мережі
export function initOfflineListeners(): void {
  if (bootstrapped) return;
  bootstrapped = true;
  window.addEventListener('online', () => {
    void offlineBootstrap();
  });
}
